import {WaveLoader} from '@wandb/weave/components/Loaders/WaveLoader';
import {TailwindContents} from '@wandb/weave/components/Tailwind';
import React, {createContext, useContext, useMemo} from 'react';
import {useLocation, useParams} from 'react-router-dom';

import NoAccessPage from '../../pages/NoAccessPage';
import {usePropsSelector} from '../../state/hooks';
import {PanelBankContextProvider} from '../../state/panelbank/context';
import {Ref as PanelRef} from '../../state/views/panel/types';
import {makePanelRefsSelector} from '../../state/views/panelBankConfig/selectors';
import {usePanelBankSectionConfigMaybe} from '../../state/views/panelBankSectionConfig/hooks';
import type {Ref as PanelBankSectionConfigRef} from '../../state/views/panelBankSectionConfig/types';
import {useRunSelectorResizingContextMaybe} from '../MultiRunWorkspace/RunSelectorResizingContext';
import {useHideNavOnPage} from '../NavContextProvider';
import {useAutoSyncPanelBankContext} from '../PanelBank/AutoSyncPanelBankContext';
import {getTrimmedTitleFromAnyConfig} from '../PanelTitle/getTitleFromConfig';
import {useWorkspaceRefsContext} from '../Workspace/WorkspaceRefsContext';
import {useWorkspaceInheritedSettingsForPanel} from '../WorkspaceDrawer/Settings/hooks/useWorkspaceInheritedSettingsForPanel';
import {SectionSettingsContextProvider} from '../WorkspaceDrawer/Settings/SectionSettingsContextProvider';
import {useWorkspaceDrawerContext} from '../WorkspaceDrawer/WorkspaceDrawerContextProvider';
import {PanelContainer} from '.';
import {FullScreenPanelNotFound} from './FullScreenPanelNotFound';
import {useConfigXBounds, useDetectPanelVariant} from './hooks';
import {usePanelContext} from './PanelContextProvider';
import {SharedPanelStateContextProvider} from './SharedPanelStateContext';

type PanelProps = {
  isReadOnly?: boolean; // whether user can edit the panel
  isProjectReadOnly?: boolean; // whether user can edit project level resources (e.g. reports)
};

type StandalonePanelInnerProps = {
  panelRef: PanelRef;
  panelBankSectionConfigRef: PanelBankSectionConfigRef;
  mode: 'embedded' | 'full-screen-workspace';
  isReadOnly?: boolean; // whether user can edit the panel
  isProjectReadOnly?: boolean; // whether user can edit project level resources (e.g. reports)
};

export const usePanelRefAndPanelBankSectionConfigRef = (panelId: string) => {
  const {panelBankConfigRef} = useWorkspaceRefsContext();

  const panelRefAndPanelBankSectionConfigRef = usePropsSelector(
    makePanelRefsSelector,
    panelBankConfigRef,
    {panelId}
  );
  return panelRefAndPanelBankSectionConfigRef;
};

export const usePanelRefAndPanelBankSectionConfigRefByName = (
  panelDisplayName: string,
  panelSectionName?: string
) => {
  const {panelBankConfigRef} = useWorkspaceRefsContext();

  const panelRefAndPanelBankSectionConfigRef = usePropsSelector(
    makePanelRefsSelector,
    panelBankConfigRef,
    {
      displayName: panelDisplayName,
      sectionName: panelSectionName,
    }
  );
  return panelRefAndPanelBankSectionConfigRef;
};

export const useSharePanelLink = () => {
  const {panel, panelBankSectionConfigRef} = usePanelContext();
  const panelBankSectionConfig = usePanelBankSectionConfigMaybe(
    panelBankSectionConfigRef
  );

  const getSharePanelLink = () => {
    const searchParams = new URLSearchParams(window.location.search);
    const panelType = panel.viewType;
    let newUrl = '';
    const panelDisplayName = getTrimmedTitleFromAnyConfig(panelType, panel);
    if (panel.isAuto && panelDisplayName) {
      searchParams.set('panelDisplayName', panelDisplayName);
      if (panelBankSectionConfig?.name != null) {
        searchParams.set('panelSectionName', panelBankSectionConfig.name);
      }
      newUrl = `${window.location.pathname}?${searchParams.toString()}`;
    } else {
      newUrl = `${window.location.pathname}/panel/${
        panel.__id__
      }?${searchParams.toString()}`;
    }
    return newUrl;
  };
  return getSharePanelLink;
};

type StandalonePanelContextProviderProps = {
  children: React.ReactNode;
};

type StandalonePanelContextType = {
  panelRefAndPanelBankSectionConfigRef: {
    panelRef: PanelRef;
    panelBankSectionConfigRef: PanelBankSectionConfigRef;
  } | null;
  isPanelNotFound: boolean;
};

const StandalonePanelContext = createContext<
  StandalonePanelContextType | undefined
>(undefined);

export const StandalonePanelContextProvider = ({
  children,
}: StandalonePanelContextProviderProps) => {
  const location = useLocation();
  const {
    isFullScreenPanelById,
    isEmbeddedPanel,
    isFullScreenPanelByName,
    isFullScreenPanelByNameAndSectionName,
  } = useDetectPanelVariant();
  const {panelId} = useParams<{panelId: string}>();
  const searchParams = new URLSearchParams(location.search);
  const panelDisplayName = searchParams.get('panelDisplayName');
  const panelSectionName = searchParams.get('panelSectionName');

  const panelRefAndPanelBankSectionConfigRefFromPanelId =
    usePanelRefAndPanelBankSectionConfigRef(panelId ?? '');

  const panelRefAndPanelBankSectionConfigRefFromDisplayName =
    usePanelRefAndPanelBankSectionConfigRefByName(
      panelDisplayName ?? '',
      panelSectionName ?? ''
    );

  let panelRefAndPanelBankSectionConfigRef: {
    panelRef: PanelRef;
    panelBankSectionConfigRef: PanelBankSectionConfigRef;
  } | null = null;
  if (isFullScreenPanelById || isEmbeddedPanel) {
    panelRefAndPanelBankSectionConfigRef =
      panelRefAndPanelBankSectionConfigRefFromPanelId;
  }
  if (isFullScreenPanelByName || isFullScreenPanelByNameAndSectionName) {
    panelRefAndPanelBankSectionConfigRef =
      panelRefAndPanelBankSectionConfigRefFromDisplayName;
  }

  const value = useMemo(
    () => ({
      panelRefAndPanelBankSectionConfigRef,
      isPanelNotFound: !panelRefAndPanelBankSectionConfigRef,
    }),
    [panelRefAndPanelBankSectionConfigRef]
  );

  return (
    <StandalonePanelContext.Provider value={value}>
      {children}
    </StandalonePanelContext.Provider>
  );
};

export const useStandalonePanelContext = () => {
  const value = useContext(StandalonePanelContext);

  if (value == null) {
    throw new Error(
      'useFullScreenPanelNotFound must be used within a FullScreenPanelNotFound'
    );
  }

  return value;
};

export const StandalonePanelInner = ({
  panelRef,
  panelBankSectionConfigRef,
  mode,
  isReadOnly,
  isProjectReadOnly,
}: StandalonePanelInnerProps) => {
  const inheritedSettings = useWorkspaceInheritedSettingsForPanel();
  const configXBounds = useConfigXBounds(panelRef);
  const runSelectorResizingContext = useRunSelectorResizingContextMaybe();
  const {shouldHidePanelContents} = useWorkspaceDrawerContext();
  const shouldRenderContent =
    !shouldHidePanelContents && !runSelectorResizingContext?.isResizing;

  return (
    // PanelBankContextProvider is needed for CreateReportModal
    <PanelBankContextProvider>
      <SharedPanelStateContextProvider config={configXBounds}>
        <PanelContainer
          isReadOnly={isReadOnly}
          isProjectReadOnly={isProjectReadOnly}
          mode={mode}
          panelBankSectionConfigRef={panelBankSectionConfigRef}
          panelRef={panelRef}
          inheritedSettings={inheritedSettings}
          shouldRenderContent={shouldRenderContent}
        />
      </SharedPanelStateContextProvider>
    </PanelBankContextProvider>
  );
};

export const StandalonePanel = ({
  isReadOnly,
  isProjectReadOnly,
}: PanelProps) => {
  const {panelRefAndPanelBankSectionConfigRef, isPanelNotFound} =
    useStandalonePanelContext();
  const {isFullScreenPanelPage} = useDetectPanelVariant();
  const mode = isFullScreenPanelPage ? 'full-screen-workspace' : 'embedded';

  const {isLoading: isAutoSyncLoading} = useAutoSyncPanelBankContext();
  const isLoading = isPanelNotFound && isAutoSyncLoading;

  if (isLoading) {
    return <WaveLoader size="small" />;
  }

  if (isPanelNotFound && mode === 'embedded') {
    return <NoAccessPage />;
  }

  if (isPanelNotFound) {
    return <FullScreenPanelNotFound />;
  }

  const {panelBankSectionConfigRef, panelRef} =
    panelRefAndPanelBankSectionConfigRef!;

  return (
    <SectionSettingsContextProvider sectionRef={panelBankSectionConfigRef}>
      <StandalonePanelInner
        panelRef={panelRef}
        panelBankSectionConfigRef={panelBankSectionConfigRef}
        mode={mode}
        isReadOnly={isReadOnly}
        isProjectReadOnly={isProjectReadOnly}
      />
    </SectionSettingsContextProvider>
  );
};

export const FullPagePanel = ({isReadOnly, isProjectReadOnly}: PanelProps) => {
  return (
    <TailwindContents>
      <div className="h-full w-full overflow-auto">
        <StandalonePanel
          isReadOnly={isReadOnly}
          isProjectReadOnly={isProjectReadOnly}
        />
      </div>
    </TailwindContents>
  );
};

export const EmbeddablePanel = ({
  isReadOnly,
  isProjectReadOnly,
}: PanelProps) => {
  useHideNavOnPage();
  return (
    <StandalonePanel
      isReadOnly={isReadOnly}
      isProjectReadOnly={isProjectReadOnly}
    />
  );
};
